import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AttendanceTable from "../components/Table/AttendanceTable";
import Skeleton from "../components/UI/Skeleton";
import {
  fetchAsistenciaByRange,
  fmtFechaCorta,
  parseDMY,
  updateLeaveStatus,
  STATUS,
} from "../services/asistenciaCampoService";
import { fetchWorkSummaryPunches, sendWorkSummaryRecordIfNeeded, sendCommissionServiceRecordIfNeeded, sendIncidenceRegularizationRecordIfNeeded, sendTravelRecordIfNeeded, parseTravelPayload, travelErrorUserMessage, warmUpWorkSummaryToken, parseCommissionApplySuggestionsPayloadFromJustificacion } from "../services/workSummaryIntegrationService";
import { autoRejectInvalidServiceCommissionsFromClient } from "../services/autoRejectServiceCommissionClient";
import { notifyLeaveStatusChange } from "../services/pushNotificationsService";
import { notifyLeaveStatusChange as notifyWhatsappLeaveStatusChange } from "../services/whatsappNotificationsService";
import { parseHorasPreviewFromJustificacion } from "../services/horasPreview";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { Button, Checkbox, ConfigProvider, DatePicker, Drawer, Input, Modal, Progress, Result, Select, Tag, message } from "antd";
import esES from "antd/locale/es_ES";
import { child, get, ref } from "firebase/database";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { FiCheckCircle, FiClock, FiFilter, FiXCircle } from "react-icons/fi";
import { TbFileTypeXls } from "react-icons/tb";
import "../styles/asistenciaCampo.css";
import { useAuth } from "../contexts/loginContext";
import { ROLES_SOLO_LECTURA } from "../contexts/roles";
import { dbAsistencia } from "../firebase";

dayjs.locale("es");

const STATUS_LABELS_ES = {
  [STATUS.PENDING]: "Pendiente",
  [STATUS.APPROVED]: "Aprobado",
  [STATUS.DENIED]: "Rechazado",
};
const DEFAULT_COUNTRY_CODE = String(
  process.env.REACT_APP_DEFAULT_COUNTRY_CODE ||
    (typeof import.meta !== "undefined" && import.meta?.env?.VITE_DEFAULT_COUNTRY_CODE) ||
    ""
).trim();

const STATUS_ICON = {
  [STATUS.PENDING]: FiClock,
  [STATUS.APPROVED]: FiCheckCircle,
  [STATUS.DENIED]: FiXCircle,
};

const STATUS_ORDER = [STATUS.PENDING, STATUS.APPROVED, STATUS.DENIED];
const TICS_ADMIN_EMAIL = "tics@guamanpoma.org";
const rowKeyOf = (r) => r.id || `${r.userId}__${r.leaveId}`;
const OLVIDE_PICAR_LABEL_NORMALIZED = "olvide picar";
const TYPE_LABELS = {
  op: "No Pude Picar",
  va: "Vacaciones",
  sc: "Comisión de servicio",
  pe: "Permiso ocasional",
  pa: "Permiso personal",
  tc: "Trabajo Corrido",
  vi: "Viaje",
};
const LEGACY_TYPE_TO_NEW = {
  al: "op",
  cl: "va",
  ml: "pe",
  viaje: "vi",
};

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function isOlvidePicarRow(row) {
  const tipoKey = normalizeText(row?.tipoKey);
  if (tipoKey === "op") return true;
  return normalizeText(row?.tipoPermiso) === OLVIDE_PICAR_LABEL_NORMALIZED;
}

function normalizeTipoKey(typeRaw) {
  const t = normalizeText(typeRaw);
  return LEGACY_TYPE_TO_NEW[t] || t;
}

function tipoLabelFromRaw(typeRaw) {
  const key = normalizeTipoKey(typeRaw);
  return TYPE_LABELS[key] || String(typeRaw || "").trim().toUpperCase();
}

function confirmStatusChange({ fromStatus, toStatus, isOlvidoPicar, isViaje, row }) {
  if (fromStatus === toStatus) return Promise.resolve(true);

  const fromLabel = STATUS_LABELS_ES[fromStatus] || fromStatus;
  const toLabel = STATUS_LABELS_ES[toStatus] || toStatus;
  const isApprove = toStatus === STATUS.APPROVED;

  const statusText = isApprove
    ? `Vas a cambiar de ${fromLabel} a ${toLabel}. Esta accion no tiene retroceso operativo.${
        isOlvidoPicar
          ? " Ademas, se enviara la marcacion al microservicio."
          : isViaje
          ? " Ademas, se actualizara el historial de asistencia de las fechas del viaje."
          : ""
      }`
    : `Vas a cambiar de ${fromLabel} a ${toLabel}.`;

  const viajeFechas = isViaje ? formatDateRange(row?.desde, row?.hasta) : "";

  return new Promise((resolve) => {
    Modal.confirm({
      title: isViaje
        ? isApprove
          ? "Confirmar aprobacion de viaje"
          : "Confirmar cambio de estado del viaje"
        : isApprove
        ? "Confirmar aprobacion"
        : "Confirmar cambio de estado",
      content: isViaje ? (
        <div>
          <div style={{ marginBottom: 8 }}>
            <Tag color="geekblue">Viaje</Tag>
            <b>{row?.usuario || row?.dni || "Solicitud"}</b>
            {viajeFechas ? (
              <div style={{ marginTop: 4 }}>
                {row?.hasta && row?.desde && row.hasta.getTime() !== row.desde.getTime() ? "Fechas: " : "Fecha: "}
                <b>{viajeFechas}</b>
              </div>
            ) : null}
          </div>
          {statusText}
        </div>
      ) : (
        statusText
      ),
      okText: isApprove ? "Si, aprobar" : "Confirmar",
      cancelText: "Cancelar",
      centered: true,
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

const PROGRESS_MESSAGES = [
  "Validando la solicitud...",
  "Guardando los cambios...",
  "Actualizando los registros...",
  "Procesando las fechas...",
];
const PROGRESS_ALMOST_DONE_MESSAGES = [
  "Ya falta poco...",
  "Casi listo, gracias por tu paciencia...",
  "Terminando los últimos detalles...",
  "Un momento más...",
];

function openSwalProgress({ title, text }) {
  let percent = 0;
  let timer = null;
  let messageTimer = null;
  let messageIndex = 0;
  let finished = false;
  const render = () => {
    const bar = document.getElementById("swal-progress-bar");
    const label = document.getElementById("swal-progress-label");
    if (bar) bar.style.width = `${percent}%`;
    if (label) label.textContent = `${Math.round(percent)}%`;
  };

  Swal.fire({
    title,
    html: `
      <div style="margin-bottom:6px">${text}</div>
      <div id="swal-progress-text" style="margin-bottom:12px;color:#8c8c8c;font-size:14px;min-height:20px">${PROGRESS_MESSAGES[0]}</div>
      <div style="background:#f0f0f0;border-radius:999px;height:10px;overflow:hidden">
        <div id="swal-progress-bar" style="background:#1677ff;height:100%;width:0%;transition:width .2s ease"></div>
      </div>
      <div id="swal-progress-label" style="margin-top:8px;font-weight:600">0%</div>
    `,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      if (finished) return;
      // Avance simulado hasta 95%; el 100% solo se marca cuando el servidor responde
      timer = setInterval(() => {
        percent = Math.min(95, percent + Math.max(0.5, (95 - percent) * 0.08));
        render();
      }, 200);
      // Mensaje dinamico cada 2s; al llegar a 95% cambia a mensajes de "ya falta poco"
      messageTimer = setInterval(() => {
        const messages = percent >= 95 ? PROGRESS_ALMOST_DONE_MESSAGES : PROGRESS_MESSAGES;
        messageIndex += 1;
        const el = document.getElementById("swal-progress-text");
        if (el) el.textContent = messages[messageIndex % messages.length];
      }, 2000);
    },
  });

  return {
    complete: async () => {
      finished = true;
      clearInterval(timer);
      clearInterval(messageTimer);
      const el = document.getElementById("swal-progress-text");
      if (el) el.textContent = "¡Listo!";
      percent = 100;
      render();
      await new Promise((resolve) => setTimeout(resolve, 300));
    },
    close: () => {
      finished = true;
      clearInterval(timer);
      clearInterval(messageTimer);
      Swal.close();
    },
  };
}

function formatDateRange(desde, hasta) {
  if (!desde) return "";
  const dDesde = fmtFechaCorta(desde);
  if (!hasta || desde.getTime() === hasta.getTime()) {
    return dDesde;
  }
  return `${dDesde} - ${fmtFechaCorta(hasta)}`;
}

function isTicsAdmin(user, role) {
  return role === "admin" && String(user?.email || "").trim().toLowerCase() === TICS_ADMIN_EMAIL;
}

function renderSolicitudSummary(row) {
  const solicitado = row?.appliedDate ? fmtFechaCorta(parseDMY(row.appliedDate)) : "-";
  const fecha = formatDateRange(row?.desde, row?.hasta) || "-";
  const tipo = String(row?.tipoPermiso || "").trim() || "-";

  return (
    <div className="solicitud-meta" title={`Solicitado: ${solicitado} | Fecha: ${fecha} | Tipo: ${tipo}`}>
      <span className="solicitud-meta__item">
        <span className="solicitud-meta__label">Solicitado</span>
        <span className="solicitud-meta__value">{solicitado}</span>
      </span>
      <span className="solicitud-meta__item">
        <span className="solicitud-meta__label">Fecha</span>
        <span className="solicitud-meta__value">{fecha}</span>
      </span>
      <span className="solicitud-meta__item">
        <span className="solicitud-meta__label">Tipo</span>
        <span className="solicitud-meta__value">{tipo}</span>
      </span>
    </div>
  );
}

function getHorarioTone(label) {
  const raw = String(label || "").toLowerCase();
  if (raw.includes("entrada") && raw.includes("m")) return "entrada-am";
  if (raw.includes("salida") && raw.includes("m")) return "salida-am";
  if (raw.includes("entrada") && raw.includes("t")) return "entrada-pm";
  if (raw.includes("salida") && raw.includes("t")) return "salida-pm";
  return "default";
}

function workSummaryKey(row) {
  const date = row?.desde;
  return date && row?.dni ? `${row.dni}__${date.toISOString().slice(0, 10)}` : "";
}

function renderRowDetails(row, workSummary) {
  const responsable = String(row?.responsable || "").trim() || "-";
  const solicitado = row?.appliedDate ? fmtFechaCorta(parseDMY(row.appliedDate)) : "-";
  const slots = parseHorasPreviewFromJustificacion(
    row?.justificacion,
    isOlvidePicarRow(row)
  );
  const apiSlots = workSummary
    ? [
        ["Entrada", workSummary.firstEntry],
        ["Salida almuerzo", workSummary.lunchOut],
        ["Regreso almuerzo", workSummary.lunchIn],
        ["Salida final", workSummary.finalOut],
      ].map(([label, hora]) => ({ label, hora: String(hora || "").trim() || "-" }))
    : [];
  const displaySlots = slots.length > 0 ? slots : apiSlots;
  const loadingWorkSummary = workSummary?.loading === true;

  return (
    <div className="asistencia-row-details">
      <div className="asistencia-row-details__grid">
        <div className="asistencia-row-details__item">
          <span className="asistencia-row-details__label">Solicitado</span>
          <span className="asistencia-row-details__value">{solicitado}</span>
        </div>
        <div className="asistencia-row-details__item">
          <span className="asistencia-row-details__label">Responsable</span>
          <span className="asistencia-row-details__value">{responsable}</span>
        </div>
        <div className="asistencia-row-details__item asistencia-row-details__item--horarios">
          <span className="asistencia-row-details__label">Horarios completos</span>
          {loadingWorkSummary ? (
            <div className="horas-picado horas-picado--modern" aria-label="Cargando horarios completos">
              {Array.from({ length: 4 }).map((_, index) => (
                <span className="horas-picado__item horas-picado__item--modern" key={index}>
                  <Skeleton className="horas-picado__skeleton" height={28} radius={6} />
                </span>
              ))}
            </div>
          ) : displaySlots.length > 0 ? (
            <div className="horas-picado horas-picado--modern">
              {displaySlots.map((slot) => (
                <span
                  key={`${slot.label}-${slot.hora}`}
                  className={`horas-picado__item horas-picado__item--modern horas-picado__item--${getHorarioTone(slot.label)}`}
                >
                  <span className="horas-picado__topline">
                    <FiClock className="horas-picado__icon" />
                    <span className="horas-picado__label">{slot.label}</span>
                  </span>
                  <span className="horas-picado__hora">{slot.hora}</span>
                </span>
              ))}
            </div>
          ) : (
            <span className="asistencia-row-details__value">-</span>
          )}
        </div>
      </div>
    </div>
  );
}

function AsistenciaCampoSkeleton() {
  return (
    <div className="campo-table-skeleton" aria-label="Cargando solicitudes" aria-busy="true">
      <div className="campo-table-skeleton__toolbar">
        <Skeleton width={180} height={18} radius={999} />
        <Skeleton width={320} height={40} radius={10} />
      </div>
      <div className="campo-table-skeleton__header">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} width="55%" height={12} radius={999} />
        ))}
      </div>
      {Array.from({ length: 5 }).map((_, rowIndex) => (
        <div className="campo-table-skeleton__row" key={rowIndex}>
          {Array.from({ length: 5 }).map((_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              width={columnIndex === 2 ? "88%" : "70%"}
              height={16}
              radius={999}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function parseDDMMYYYYToDate(s) {
  if (!s) return null;
  const str = String(s).trim();
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(str);
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  const d = new Date(yyyy, mm - 1, dd);
  return Number.isFinite(d.getTime()) ? d : null;
}

function parseDDMMYYYYToTS(s) {
  const d = parseDDMMYYYYToDate(s);
  return d ? d.getTime() : 0;
}

function rowMatchesGlobalQuery(row, query) {
  const q = normalizeText(query);
  if (!q) return true;

  const estadoValue = Number(row?.withdrawalStatus ?? 0) === 1
    ? "Retirado"
    : STATUS_LABELS_ES[row?.estado || STATUS.PENDING] || "Pendiente";

  const joined = [
    row?.dni,
    row?.usuario,
    row?.responsable,
    row?.appliedDate ? fmtFechaCorta(parseDMY(row.appliedDate)) : "",
    formatDateRange(row?.desde, row?.hasta),
    row?.tipoPermiso,
    row?.justificacion,
    estadoValue,
  ]
    .map((x) => String(x || "").trim())
    .join(" | ");

  return normalizeText(joined).includes(q);
}

function parseHastaTimeFromJustificacion(text) {
  const raw = String(text || "").trim();
  if (!raw) return null;
  const m = /hasta\s*([0-2]\d|--):([0-5]\d|--)/i.exec(raw);
  if (!m) return null;
  if (m[1] === "--" || m[2] === "--") {
    return { missing: true };
  }
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return { hh, mm, missing: false };
}

function resolveBaseDateForCommission(row, fallbackDate) {
  if (row?.desde instanceof Date && !Number.isNaN(row.desde.getTime())) return new Date(row.desde);
  if (row?.hasta instanceof Date && !Number.isNaN(row.hasta.getTime())) return new Date(row.hasta);
  if (fallbackDate instanceof Date && !Number.isNaN(fallbackDate.getTime())) {
    return new Date(fallbackDate);
  }
  const fromApplied = parseDMY(row?.appliedDate);
  if (fromApplied && !Number.isNaN(fromApplied.getTime())) return fromApplied;
  return new Date();
}

function getCommissionVisualAlert(row, nowTs, fallbackDate) {
  if (String(row?.tipoKey || "").toLowerCase() !== "sc") return null;

  const t = parseHastaTimeFromJustificacion(row?.justificacion);
  if (!t) return null;
  if (t.missing) {
    return { className: "danger", label: "Falta hora de fin" };
  }

  const base = resolveBaseDateForCommission(row, fallbackDate);
  base.setHours(t.hh, t.mm, 0, 0);
  const remainingMs = base.getTime() - nowTs;
  const remainingMin = Math.floor(remainingMs / 60000);

  if (remainingMin >= 0 && remainingMin <= 60) {
    return { className: "danger", label: `Falta ${remainingMin} min` };
  }
  return null;
}

/**
 * Clean justification text by removing PICO and HORAS metadata
 * Example: "Desde 07:00 Hasta 16:30 - gpa |PICO:1110|HORAS:1:1:07:00,2:1:14:30,3:1:15:30,4:0:16:30"
 * or: "Desde 05:30 Hasta 17:30 - gpaHORAS:1:1:05:30,2:1:15:30,3:1:16:30,4:1:17:30"
 * becomes: "Desde 07:00 Hasta 16:30 - gpa"
 */
function cleanJustificationText(text) {
  if (!text) return "";
  // Remove |PICO:...| patterns (with pipes)
  // Remove |HORAS:...| patterns (with pipes)
  // Remove HORAS:... at end or before pipe (without pipes)
  return String(text)
    .replace(/\s*\|PICO:[^|]*\|/gi, "")
    .replace(/\s*\|HORAS:[^|]*\|/gi, "")
    .replace(/\s*HORAS:[^|]*$/gi, "")
    .trim();
}

function showWarningModal(message) {
  Modal.warning({
    title: "Advertencia",
    content: String(message || "Revisa la información e intenta nuevamente."),
    okText: "Entendido",
    centered: true,
  });
}

function formatTimeToAmPm(hh, mm) {
  const h = Number(hh);
  const m = Number(mm);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
}

function formatJustificationTimeToAmPm(text) {
  return String(text || "").replace(
    /\b([01]?\d|2[0-3]):([0-5]\d)\b(?!\s*(?:AM|PM)\b)/gi,
    (match, hh, mm) => {
      const ampm = formatTimeToAmPm(hh, mm);
      return ampm || match;
    }
  );
}

function parseTimeTokenForRange(token, { allowMissing = false } = {}) {
  const raw = String(token || "").trim().toLowerCase();
  const compact = raw.replace(/\s+/g, "");
  if (allowMissing && compact === "--:--") return { missing: true };

  const m = /^(\d{1,2}):(\d{1,2})(?:([ap])\.?m\.?)?$/.exec(compact);
  if (!m) return null;
  let hh = Number(m[1]);
  const mm = Number(m[2]);
  const mer = m[3] || null;
  if (!Number.isFinite(hh) || !Number.isFinite(mm) || mm < 0 || mm > 59) return null;

  if (mer) {
    if (hh < 1 || hh > 12) return null;
    if (mer === "a") hh = hh === 12 ? 0 : hh;
    if (mer === "p") hh = hh === 12 ? 12 : hh + 12;
  } else if (hh < 0 || hh > 23) {
    return null;
  }

  return { hh, mm, missing: false };
}

function validateDesdeHastaRange(text) {
  const src = String(text || "").replace(/\s+/g, " ").trim();
  if (!src) return { hasRange: false, ok: true, missingEnd: false };

  const m = /desde\s*([0-9]{1,2}\s*:\s*[0-9]{1,2}(?:\s*[ap]\.?\s*m\.?)?)\s*hasta\s*((?:[0-9]{1,2}|--)\s*:\s*(?:[0-9]{1,2}|--)(?:\s*[ap]\.?\s*m\.?)?)/i.exec(src);
  if (!m) return { hasRange: false, ok: true, missingEnd: false };

  const fromParsed = parseTimeTokenForRange(m[1]);
  if (!fromParsed || fromParsed.missing) return { hasRange: true, ok: false, missingEnd: false };

  const toParsed = parseTimeTokenForRange(m[2], { allowMissing: true });
  if (!toParsed) return { hasRange: true, ok: false, missingEnd: false };
  if (toParsed.missing) return { hasRange: true, ok: false, missingEnd: true };

  return { hasRange: true, ok: true, missingEnd: false };
}

function validateCommissionReturnTime(text) {
  const range = validateDesdeHastaRange(text);
  const hasReturnSlot = /(?:^|,)4:1:\d{1,2}:\d{2}(?::\d{2})?(?:,|$)/i.test(
    String(text || "").replace(/\s+/g, "")
  );
  return (range.hasRange && range.ok) || hasReturnSlot;
}

function buildSheetData(rowsArray, { includeResponsable }) {
  return (rowsArray || []).map((r) => {
    const isWithdrawn = Number(r.withdrawalStatus ?? 0) === 1;

    const base = {
      DNI: r.dni || "",
      Usuario: r.usuario || "",
      Responsable: r.responsable || "",
      Solicitado: r.appliedDate ? String(fmtFechaCorta(parseDMY(r.appliedDate))) : "",
      Fecha: formatDateRange(r.desde, r.hasta),
      "Tipo permiso": r.tipoPermiso || "",
      "Justificación": r.justificacion || "",
      Estado: isWithdrawn
        ? "Retirado"
        : STATUS_LABELS_ES[r.estado || STATUS.PENDING] || "Pendiente",
    };

    if (includeResponsable) return base;

    const { Responsable, ...rest } = base;
    return rest;
  });
}

function exportRangeToExcel(rowsToExport, filename, options) {
  const ws = XLSX.utils.json_to_sheet(buildSheetData(rowsToExport, options));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Asistencia".substring(0, 31));
  XLSX.writeFile(wb, filename);
}

export default function AsistenciaCampo() {
  const { role, managerCode, hrKey, user } = useAuth();
  const isManager = role === "manager";
  const isViewer = ROLES_SOLO_LECTURA.includes(role);
  const canEditStatus = role === "admin" || role === "manager";

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [loadMonthToDate, setLoadMonthToDate] = useState(false);

  const [estadoFilter, setEstadoFilter] = useState(STATUS.PENDING);
  const [tipoFilter, setTipoFilter] = useState("all");
  const [responsableFilter, setResponsableFilter] = useState("all");
  const [usuarioFilter, setUsuarioFilter] = useState("all");
  const [globalQuery, setGlobalQuery] = useState("");
  const [tablePageSize, setTablePageSize] = useState(10);
  const [showOlvidePicar, setShowOlvidePicar] = useState(true);

  const [rows, setRows] = useState([]);
  const [workSummaries, setWorkSummaries] = useState({});
  const workSummaryRequests = useRef(new Set());
  const [globalTipos, setGlobalTipos] = useState([]);
  const [globalResponsables, setGlobalResponsables] = useState([]);
  const [globalUsuarios, setGlobalUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [excludedBulkKeys, setExcludedBulkKeys] = useState([]);
  const [tableVisibleRows, setTableVisibleRows] = useState([]);
  const [autoRejectChecked, setAutoRejectChecked] = useState(false);
  const [highlightToDateStep, setHighlightToDateStep] = useState(false);
  const [expandedJustificationKeys, setExpandedJustificationKeys] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const bulkProgressKey = bulkProgress?.currentKey;
  const bulkProgressDone = bulkProgress?.done;
  const bulkProgressTotal = bulkProgress?.total || 0;
  const bulkProgressIndex = bulkProgress?.currentIndex || 0;

  useEffect(() => {
    if (!bulkProgressKey || bulkProgressDone) return;

    const itemPreviewEnd = Math.min(bulkProgressIndex + 0.9, bulkProgressTotal);
    const timer = setInterval(() => {
      setBulkProgress((progress) =>
        !progress || progress.done
          ? progress
          : { ...progress, processed: Math.min(progress.processed + 0.025, itemPreviewEnd) }
      );
    }, 120);

    return () => clearInterval(timer);
  }, [bulkProgressKey, bulkProgressDone, bulkProgressIndex, bulkProgressTotal]);

  // Token de la API de asistencia listo antes de aprobar; getBearerToken solo hace login si esta por vencer
  useEffect(() => {
    const warmUp = () =>
      warmUpWorkSummaryToken().catch((err) => {
        console.warn("No se pudo precargar el token de la API de asistencia:", err);
      });
    warmUp();
    const timer = setInterval(warmUp, 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const handleCalendarInteract = () => {
    if (loadMonthToDate) {
      setLoadMonthToDate(false);
    }
  };

  const dateRange = useMemo(() => {
    if (loadMonthToDate) {
      const now = new Date();
      const from = new Date(now);
      const to = new Date(now);
      from.setDate(1);
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }

    const fromBase =
      fromDate instanceof Date && !Number.isNaN(fromDate.getTime())
        ? new Date(fromDate)
        : null;
    const toBase =
      toDate instanceof Date && !Number.isNaN(toDate.getTime())
        ? new Date(toDate)
        : fromBase
        ? new Date(fromBase)
        : null;

    if (!fromBase || !toBase) {
      const now = new Date();
      const from = new Date(now);
      const to = new Date(now);
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }

    const from = new Date(fromBase);
    from.setHours(0, 0, 0, 0);

    const to = new Date(toBase);
    to.setHours(23, 59, 59, 999);

    if (to.getTime() < from.getTime()) {
      return { from, to: new Date(from.getTime() + (23 * 60 * 60 * 1000 + 59 * 60 * 1000 + 59 * 1000 + 999)) };
    }

    return { from, to };
  }, [fromDate, toDate, loadMonthToDate]);

  useEffect(() => {
    if (!loadMonthToDate) return;
    const today = new Date();
    const first = new Date(today);
    first.setDate(1);
    first.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    setFromDate(first);
    setToDate(today);
  }, [loadMonthToDate]);

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!highlightToDateStep) return;
    const id = setTimeout(() => setHighlightToDateStep(false), 1800);
    return () => clearTimeout(id);
  }, [highlightToDateStep]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchAsistenciaByRange(dateRange.from, dateRange.to);
        setRows(data || []);
      } catch (e) {
        console.error(e);
        setError("No se pudieron cargar los datos.");
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [dateRange]);

  useEffect(() => {
    const loadGlobalFilterCatalog = async () => {
      try {
        const root = ref(dbAsistencia);
        const [usersSnap, managersSnap, leavesSnap] = await Promise.all([
          get(child(root, "users")),
          get(child(root, "managers")),
          get(child(root, "leaves")),
        ]);

        const users = usersSnap.exists() ? usersSnap.val() : {};
        const managers = managersSnap.exists() ? managersSnap.val() : {};
        const leaves = leavesSnap.exists() ? leavesSnap.val() : {};

        const responsableSet = new Set();
        Object.values(managers || {}).forEach((m) => {
          const name = String(m?.name || "").trim();
          if (name) responsableSet.add(name);
        });
        setGlobalResponsables(Array.from(responsableSet).sort((a, b) => a.localeCompare(b)));

        const userMap = new Map();
        Object.values(users || {}).forEach((u) => {
          const usuario = String(u?.Name || "").trim();
          if (!usuario) return;
          const dni = String(u?.UUID ?? "").trim();
          if (!userMap.has(usuario)) {
            userMap.set(usuario, dni);
          }
        });

        const usuarios = Array.from(userMap.entries())
          .map(([usuario, dni]) => ({
            value: usuario,
            label: dni ? `${usuario} - ${dni}` : usuario,
          }))
          .sort((a, b) => a.value.localeCompare(b.value));

        setGlobalUsuarios(usuarios);

        const tipoSet = new Set();
        Object.values(leaves || {}).forEach((userLeaves) => {
          Object.values(userLeaves || {}).forEach((leave) => {
            const label = tipoLabelFromRaw(leave?.type);
            if (label) tipoSet.add(label);
          });
        });
        setGlobalTipos(Array.from(tipoSet).sort((a, b) => a.localeCompare(b)));
      } catch (e) {
        console.error("loadGlobalFilterCatalog error:", e);
        setGlobalTipos([]);
        setGlobalResponsables([]);
        setGlobalUsuarios([]);
      }
    };

    loadGlobalFilterCatalog();
  }, []);

  useEffect(() => {
    const isRootAdmin = String(hrKey || "") === "0";
    const canRunAutoReject = (role === "admin" || role === "manager") && !isRootAdmin;
    if (!canRunAutoReject || autoRejectChecked) return;
    setAutoRejectChecked(true);

    (async () => {
      try {
        const result = await autoRejectInvalidServiceCommissionsFromClient({
          timeZone: "America/Bogota",
        });
        if (result?.rejected > 0) {
          const data = await fetchAsistenciaByRange(dateRange.from, dateRange.to);
          setRows(data || []);
        }
      } catch (e) {
        console.error("autoRejectInvalidServiceCommissionsFromClient error:", e);
      }
    })();
  }, [role, hrKey, autoRejectChecked, dateRange]);

  useEffect(() => {
    if (isViewer && showOlvidePicar) {
      setShowOlvidePicar(false);
    }
  }, [isViewer, showOlvidePicar]);

  const shouldHideOlvidePicar = isViewer || !showOlvidePicar;

  useEffect(() => {
    if (shouldHideOlvidePicar && normalizeText(tipoFilter) === OLVIDE_PICAR_LABEL_NORMALIZED) {
      setTipoFilter("all");
    }
  }, [shouldHideOlvidePicar, tipoFilter]);

  const tipoOptions = useMemo(() => {
    const source =
      globalTipos.length > 0
        ? globalTipos
        : Array.from(
            new Set(
              (rows || [])
                .map((r) => String(r?.tipoPermiso || "").trim())
                .filter(Boolean)
            )
          );

    const visible = source.filter((t) => {
      if (shouldHideOlvidePicar && normalizeText(t) === OLVIDE_PICAR_LABEL_NORMALIZED) {
        return false;
      }
      return true;
    });

    return ["all", ...visible.sort((a, b) => a.localeCompare(b))];
  }, [globalTipos, rows, shouldHideOlvidePicar]);

  const responsableOptions = useMemo(() => {
    if (globalResponsables.length > 0) {
      return ["all", ...globalResponsables];
    }
    const set = new Set();
    (rows || []).forEach((r) => {
      const name = String(r?.responsable || "").trim();
      if (name) set.add(name);
    });
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [globalResponsables, rows]);

  const usuarioOptions = useMemo(() => {
    if (globalUsuarios.length > 0) return globalUsuarios;
    const map = new Map();
    (rows || []).forEach((r) => {
      const usuario = String(r?.usuario || "").trim();
      if (!usuario) return;
      const dni = String(r?.dni || "").trim();
      if (!map.has(usuario)) map.set(usuario, dni);
    });
    return Array.from(map.entries())
      .map(([usuario, dni]) => ({
        value: usuario,
        label: dni ? `${usuario} - ${dni}` : usuario,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [globalUsuarios, rows]);

  const changeEstado = async (row, newLabel) => {
    const isWithdrawn = Number(row.withdrawalStatus ?? 0) === 1;
    const currentStatus = row?.estado || STATUS.PENDING;
    const isLockedFinalStatus =
      currentStatus === STATUS.APPROVED || currentStatus === STATUS.DENIED;
    const canReopenApproved = isTicsAdmin(user, role) && currentStatus === STATUS.APPROVED;
    if (isWithdrawn) {
      return;
    }
    if (isLockedFinalStatus && !canReopenApproved) {
      return;
    }
    if (canReopenApproved && newLabel !== STATUS.PENDING && newLabel !== STATUS.APPROVED) {
      return;
    }

    const k = rowKeyOf(row);
    const prev = row.estado;
    const confirmed = await confirmStatusChange({
      fromStatus: prev || STATUS.PENDING,
      toStatus: newLabel,
      isOlvidoPicar: String(row?.tipoKey || "").toLowerCase() === "op",
      isViaje: String(row?.tipoKey || "").toLowerCase() === "vi",
      row,
    });
    if (!confirmed) {
      return;
    }

    // Validate commission service before updating status
    const typeKey = String(row?.tipoKey || "").toLowerCase();
    const isCommissionService = typeKey === "sc";
    const isPersonalPermission = typeKey === "pa";
    const isViaje = typeKey === "vi";
    const transitionedToApproved = prev !== STATUS.APPROVED && newLabel === STATUS.APPROVED;

    if (transitionedToApproved && isViaje) {
      try {
        parseTravelPayload(row);
      } catch (validationErr) {
        showWarningModal(`No se pudo aprobar: ${validationErr.message}`);
        return;
      }
    }

    if (transitionedToApproved && isCommissionService) {
      if (!validateCommissionReturnTime(row?.justificacion)) {
        showWarningModal("No se pudo aprobar: la comisión de servicio no tiene una hora de retorno válida.");
        return;
      }
      try {
        parseCommissionApplySuggestionsPayloadFromJustificacion({
          justificacion: row?.justificacion,
          dni: row?.dni,
          desde: row?.desde instanceof Date ? row.desde : null,
        });
      } catch (validationErr) {
        showWarningModal(`No se pudo aprobar: ${validationErr.message}`);
        return;
      }
    }
    if (transitionedToApproved && isPersonalPermission) {
      const rangeValidation = validateDesdeHastaRange(row?.justificacion);
      if (rangeValidation.hasRange && !rangeValidation.ok) {
        showWarningModal(
          "No se pudo aprobar: la justificación tiene rango Desde/Hasta incompleto o inválido."
        );
        return;
      }
    }

    setRows((prevRows) =>
      prevRows.map((x) => (rowKeyOf(x) === k ? { ...x, estado: newLabel } : x))
    );

    // El viaje abre el progreso de inmediato y se envia antes de las notificaciones (push/WhatsApp demoran)
    const travelProgress =
      transitionedToApproved && isViaje
        ? openSwalProgress({
            title: "Aprobando viaje...",
            text: "Actualizando tu asistencia, esto puede tomar un poco de tiempo.",
          })
        : null;

    try {
      await updateLeaveStatus(row.userId, row.leaveId, newLabel);

      if (travelProgress) {
        try {
          const travelResult = await sendTravelRecordIfNeeded({
            ...row,
            estado: newLabel,
          });
          await travelProgress.complete();
          const toDMY = (ymd) => String(ymd || "").split("-").reverse().join("/") || "-";
          const fromDMY = toDMY(travelResult?.payload?.dateFrom);
          const toDMYStr = toDMY(travelResult?.payload?.dateTo);
          const rango = fromDMY === toDMYStr ? `del ${fromDMY}` : `del ${fromDMY} al ${toDMYStr}`;
          Swal.fire({
            icon: travelResult?.skipped ? "info" : "success",
            title: travelResult?.skipped ? "Viaje ya registrado" : "Viaje registrado",
            text: travelResult?.skipped
              ? `Tu historial de asistencia ${rango} ya estaba actualizado.`
              : `Se actualizó tu historial de asistencia ${rango}.`,
            confirmButtonText: "Aceptar",
          });
        } catch (syncErr) {
          travelProgress.close();
          console.error("travel sync error:", syncErr);
          const reason = travelErrorUserMessage(syncErr);
          const safeReason = reason.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
          setError(`Solicitud aprobada, pero no se pudo actualizar la asistencia: ${reason}`);
          Swal.fire({
            icon: "error",
            title: "No se pudo actualizar la asistencia",
            html: `La solicitud <b>sí fue aprobada</b>, pero no se pudo actualizar el historial de asistencia.<br/><br/>${safeReason}<br/><br/>Intenta reenviarla más tarde.`,
            confirmButtonText: "Aceptar",
          });
        }
      }

      try {
        await notifyLeaveStatusChange({
          userId: row.userId,
          leaveId: row.leaveId,
          newStatus: newLabel,
          oldStatus: prev || STATUS.PENDING,
          changedBy: user?.uid || user?.email || role || "coordinator",
          comment: "",
        });
      } catch (pushErr) {
        console.error("notifyLeaveStatusChange error:", pushErr);
        showWarningModal(
          `Estado actualizado, pero fallo la notificacion push: ${
            pushErr?.message || "Error desconocido"
          }`
        );
      }

      try {
        const waResult = await notifyWhatsappLeaveStatusChange({ ...row, newStatus: newLabel });
        if (waResult?.finalStatus === "skipped") {
          const ccHint = DEFAULT_COUNTRY_CODE ? ` (código país por defecto: +${DEFAULT_COUNTRY_CODE})` : "";
          message.warning(`WhatsApp no enviado: ${waResult?.error || "teléfono/configuración inválida"}${ccHint}`);
        } else if (waResult?.ok && ["sent", "delivered", "read"].includes(String(waResult?.finalStatus || "").toLowerCase())) {
          message.success("WhatsApp enviado");
        } else if (["retrying", "processing", "accepted", "queued"].includes(String(waResult?.finalStatus || "").toLowerCase())) {
          message.info("Notificación WhatsApp encolada");
        } else if (waResult?.finalStatus === "dead-letter") {
          message.error(waResult?.error || "No se pudo enviar WhatsApp (número inválido/no resolvible)");
        } else {
          message.error(`No se pudo enviar WhatsApp${waResult?.error ? `: ${waResult.error}` : ""}`);
        }
      } catch (waErr) {
        message.error(`No se pudo enviar WhatsApp: ${waErr?.message || "Error desconocido"}`);
      }

      if (transitionedToApproved) {
        // Try to send to work summary microservice based on type
        try {
          const isOlvidoPicar = String(row?.tipoKey || "").toLowerCase() === "op";
          const isTrabajoCorrido = String(row?.tipoKey || "").toLowerCase() === "tc";

          if (isCommissionService) {
            await sendCommissionServiceRecordIfNeeded({
              ...row,
              estado: newLabel,
            });
          } else if (isOlvidoPicar) {
            await sendWorkSummaryRecordIfNeeded({
              ...row,
              estado: newLabel,
            });
          } else if (isTrabajoCorrido) {
            await sendIncidenceRegularizationRecordIfNeeded({
              ...row,
              estado: newLabel,
            });
          }
        } catch (syncErr) {
          console.error("workSummary sync error:", syncErr);
          const reason =
            (syncErr && syncErr.message ? String(syncErr.message) : "")
              .replace(/\s+/g, " ")
              .trim() || "Error desconocido";
          setError(
            `Estado aprobado guardado, pero fallo el envio al microservicio de marcacion: ${reason}`
          );
          showWarningModal(
            `Estado aprobado guardado, pero falló el envío al microservicio de marcación: ${reason}`
          );
        }
      }
    } catch (err) {
      travelProgress?.close();
      console.error("updateLeaveStatus error:", err);
      setRows((prevRows) =>
        prevRows.map((x) => (rowKeyOf(x) === k ? { ...x, estado: prev } : x))
      );
      setError("No se pudo actualizar el estado en Firebase.");
    }
  };

  const filteredRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    let scoped = rows;

    if (role === "manager" && managerCode) {
      scoped = scoped.filter((r) => {
        const rowManager = r.managerCode || r.manager || null;
        return rowManager === managerCode;
      });
    }

    if (shouldHideOlvidePicar) {
      scoped = scoped.filter((r) => !isOlvidePicarRow(r));
    }

    if (estadoFilter !== "all") {
      scoped = scoped.filter((r) => {
        const isWithdrawn = Number(r.withdrawalStatus ?? 0) === 1;
        if (isWithdrawn) return false;
        return (r.estado || STATUS.PENDING) === estadoFilter;
      });
    }

    if (tipoFilter !== "all") {
      scoped = scoped.filter((r) => {
        const t = (r?.tipoPermiso ?? "").toString().trim();
        return t === tipoFilter;
      });
    }

    if (responsableFilter !== "all") {
      scoped = scoped.filter((r) => String(r?.responsable || "").trim() === responsableFilter);
    }

    if (usuarioFilter !== "all") {
      scoped = scoped.filter((r) =>
        normalizeText(r?.usuario) === normalizeText(usuarioFilter)
      );
    }

    if (globalQuery.trim()) {
      scoped = scoped.filter((r) => rowMatchesGlobalQuery(r, globalQuery));
    }

    const sorted = [...scoped].sort((a, b) => {
      const ta = parseDDMMYYYYToTS(a.appliedDate);
      const tb = parseDDMMYYYYToTS(b.appliedDate);

      if (ta === 0 && tb === 0) return 0;
      if (ta === 0) return 1;
      if (tb === 0) return -1;
      if (tb !== ta) return tb - ta;

      const ida = String(rowKeyOf(a));
      const idb = String(rowKeyOf(b));
      return idb.localeCompare(ida);
    });

    return sorted;
  }, [
    rows,
    role,
    managerCode,
    estadoFilter,
    tipoFilter,
    responsableFilter,
    usuarioFilter,
    globalQuery,
    shouldHideOlvidePicar,
  ]);

  const approvableVisibleRows = useMemo(() => {
    return tableVisibleRows.filter((r) => {
      const isWithdrawn = Number(r.withdrawalStatus ?? 0) === 1;
      if (isWithdrawn) return false;
      return (r.estado || STATUS.PENDING) === STATUS.PENDING;
    });
  }, [tableVisibleRows]);

  useEffect(() => {
    const visibleKeys = approvableVisibleRows.map((r) => rowKeyOf(r));
    setExcludedBulkKeys((prev) => {
      const visibleSet = new Set(visibleKeys);
      const next = prev.filter((key) => visibleSet.has(key));
      visibleKeys.forEach((key) => {
        if (!prev.includes(key)) next.push(key);
      });
      return next;
    });
  }, [approvableVisibleRows]);

  const selectedApprovableVisibleRows = useMemo(() => {
    const excluded = new Set(excludedBulkKeys);
    return approvableVisibleRows.filter((r) => !excluded.has(rowKeyOf(r)));
  }, [approvableVisibleRows, excludedBulkKeys]);

  const confirmBulkApprove = (count) =>
    new Promise((resolve) => {
      Modal.confirm({
        title: "Confirmar aprobacion masiva",
        content: `Se aprobaran ${count} solicitud(es) visibles segun tus filtros actuales.`,
        okText: "Si, aprobar visibles",
        cancelText: "Cancelar",
        centered: true,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const handleBulkApproveVisible = async () => {
    if (!canEditStatus || bulkApproving) return;
    const total = selectedApprovableVisibleRows.length;
    if (!total) {
      showWarningModal("No hay solicitudes seleccionadas por aprobar.");
      return;
    }

    const confirmed = await confirmBulkApprove(total);
    if (!confirmed) return;

    setBulkApproving(true);
    setBulkProgress({ total, processed: 0, currentIndex: 0, currentKey: "", currentUser: "", ok: 0, failed: 0, done: false });

    let ok = 0;
    let failed = 0;
    let syncFailed = 0;
    let pushNotifyFailed = 0;
    let whatsappFailed = 0;
    let whatsappSkipped = 0;
    let travelOk = 0;
    let travelFailed = 0;
    let validationErrors = [];

    for (const [index, row] of selectedApprovableVisibleRows.entries()) {
      const k = rowKeyOf(row);
      setBulkProgress((progress) => ({
        ...progress,
        processed: index,
        currentIndex: index,
        currentKey: k,
        currentUser: row.usuario || row.dni || "Solicitud",
      }));
      const prev = row.estado || STATUS.PENDING;

      // Validate commission service before updating status
      const typeKey = String(row?.tipoKey || "").toLowerCase();
      const isCommissionService = typeKey === "sc";
      const isPersonalPermission = typeKey === "pa";
      if (typeKey === "vi") {
        try {
          parseTravelPayload(row);
        } catch (validationErr) {
          validationErrors.push({
            dni: row.dni,
            nombre: row.nombre,
            error: validationErr.message,
          });
          continue; // Skip this row
        }
      }
      if (isCommissionService) {
        if (!validateCommissionReturnTime(row?.justificacion)) {
          validationErrors.push({
            dni: row.dni,
            nombre: row.nombre,
            error: "La comisión de servicio no tiene una hora de retorno válida.",
          });
          continue;
        }
        try {
          parseCommissionApplySuggestionsPayloadFromJustificacion({
            justificacion: row?.justificacion,
            dni: row?.dni,
            desde: row?.desde instanceof Date ? row.desde : null,
          });
        } catch (validationErr) {
          validationErrors.push({
            dni: row.dni,
            nombre: row.nombre,
            error: validationErr.message,
          });
          continue; // Skip this row
        }
      }
      if (isPersonalPermission) {
        const rangeValidation = validateDesdeHastaRange(row?.justificacion);
        if (rangeValidation.hasRange && !rangeValidation.ok) {
          validationErrors.push({
            dni: row.dni,
            nombre: row.nombre,
            error: "Rango Desde/Hasta incompleto o inválido en permiso personal.",
          });
          continue; // Skip this row
        }
      }

      setRows((prevRows) =>
        prevRows.map((x) => (rowKeyOf(x) === k ? { ...x, estado: STATUS.APPROVED } : x))
      );

      try {
        await updateLeaveStatus(row.userId, row.leaveId, STATUS.APPROVED);
        ok += 1;

        // Igual que en la aprobacion individual: el viaje se envia antes de las notificaciones
        if (typeKey === "vi") {
          try {
            await sendTravelRecordIfNeeded({
              ...row,
              estado: STATUS.APPROVED,
            });
            travelOk += 1;
          } catch (syncErr) {
            syncFailed += 1;
            travelFailed += 1;
            console.error("travel sync error (bulk):", syncErr);
          }
        }

        try {
          await notifyLeaveStatusChange({
            userId: row.userId,
            leaveId: row.leaveId,
            newStatus: STATUS.APPROVED,
            oldStatus: prev || STATUS.PENDING,
            changedBy: user?.uid || user?.email || role || "coordinator",
            comment: "Aprobado por coordinacion",
          });
        } catch (pushErr) {
          pushNotifyFailed += 1;
          console.error("notifyLeaveStatusChange error (bulk):", pushErr);
        }

        try {
          const waResult = await notifyWhatsappLeaveStatusChange({
            ...row,
            newStatus: STATUS.APPROVED,
          });
          if (waResult?.finalStatus === "skipped") {
            whatsappSkipped += 1;
          } else if (!waResult?.ok) {
            whatsappFailed += 1;
          }
        } catch (waErr) {
          whatsappFailed += 1;
          console.error("notifyWhatsappStatusChange error (bulk):", waErr);
        }

        try {
          const isOlvidoPicar = String(row?.tipoKey || "").toLowerCase() === "op";
          const isTrabajoCorrido = String(row?.tipoKey || "").toLowerCase() === "tc";

          if (isCommissionService) {
            await sendCommissionServiceRecordIfNeeded({
              ...row,
              estado: STATUS.APPROVED,
            });
          } else if (isOlvidoPicar) {
            await sendWorkSummaryRecordIfNeeded({
              ...row,
              estado: STATUS.APPROVED,
            });
          } else if (isTrabajoCorrido) {
            await sendIncidenceRegularizationRecordIfNeeded({
              ...row,
              estado: STATUS.APPROVED,
            });
          }
        } catch (syncErr) {
          syncFailed += 1;
          console.error("workSummary sync error (bulk):", syncErr);
        }
      } catch (err) {
        failed += 1;
        console.error("updateLeaveStatus error (bulk):", err);
        setRows((prevRows) =>
          prevRows.map((x) => (rowKeyOf(x) === k ? { ...x, estado: prev } : x))
        );
      }
    }

    setBulkApproving(false);
    setBulkProgress({
      total,
      processed: total,
      currentIndex: total,
      currentKey: "",
      currentUser: "",
      ok,
      failed: failed + validationErrors.length,
      travelOk,
      travelFailed,
      done: true,
    });

    if (
      validationErrors.length > 0 ||
      failed > 0 ||
      syncFailed > 0 ||
      pushNotifyFailed > 0 ||
      whatsappFailed > 0 ||
      whatsappSkipped > 0
    ) {
      let msg = "";
      if (validationErrors.length > 0) {
        msg += `${validationErrors.length} solicitud(es) no aprobadas por datos inválidos. `;
        msg += `${validationErrors.map((e) => `DNI ${e.dni}: ${e.error}`).join(" | ")}. `;
      }
      if (failed > 0) msg += `Aprobadas: ${ok}. Fallidas: ${failed}. `;
      if (syncFailed > 0) msg += `Sincronizacion microservicio con error: ${syncFailed}.`;
      if (pushNotifyFailed > 0) {
        msg += ` Notificacion push fallida en ${pushNotifyFailed} solicitud(es).`;
      }
      if (whatsappSkipped > 0) {
        msg += ` WhatsApp omitido en ${whatsappSkipped} solicitud(es) por celular inválido/configuración.`;
      }
      if (whatsappFailed > 0) {
        msg += ` No se pudo encolar WhatsApp en ${whatsappFailed} solicitud(es).`;
      }
      setError(msg);
    } else {
      setError("");
    }
  };

  const toggleExcludeOne = (row, checked) => {
    const k = rowKeyOf(row);
    setExcludedBulkKeys((prev) => {
      const set = new Set(prev);
      if (checked) set.delete(k);
      else set.add(k);
      return Array.from(set);
    });
  };

  const toggleAllVisible = (checked) => {
    const visibleKeys = approvableVisibleRows.map((r) => rowKeyOf(r));
    setExcludedBulkKeys((prev) => {
      const set = new Set(prev);
      if (checked) {
        visibleKeys.forEach((k) => set.delete(k));
      } else {
        visibleKeys.forEach((k) => set.add(k));
      }
      return Array.from(set);
    });
  };

  const columns = useMemo(() => {
    const base = [
      { key: "dni", header: "DNI", width: 110, ellipsis: true },
      { key: "usuario", header: "Usuario", width: 150, ellipsis: true },
      {
        key: "fecha",
        header: "Fecha",
        width: 130,
        ellipsis: true,
        render: (r) => formatDateRange(r?.desde, r?.hasta),
      },
      {
        key: "phoneNumber",
        header: "Celular",
        width: 120,
        ellipsis: true,
        render: (r) => String(r?.phoneNumber || "").trim() || "-",
      },
      { key: "tipoPermiso", header: "Tipo", width: 110, ellipsis: true },
      {
        key: "justificacion",
        header: "Justificación",
        width: 430,
        render: (r) => {
          const fallbackForAlert = toDate || fromDate || new Date();
          const alertInfo = getCommissionVisualAlert(r, nowTs, fallbackForAlert);
          const cleanText = cleanJustificationText(r?.justificacion);
          const finalText = formatJustificationTimeToAmPm(cleanText);
          const isExpanded = expandedJustificationKeys.includes(rowKeyOf(r));
          return (
            <div
              className={
                "justificacion-text" + (alertInfo ? " justificacion-alert" : "")
              }
            >
              <span
                className={
                  "justificacion-text__content " +
                  (isExpanded ? "expanded" : "clamped")
                }
                title={finalText || "-"}
                onClick={() => toggleJustificationExpand(r)}
              >
                {finalText || "-"}
              </span>
              <span className="justificacion-text__cta-mobile">
                {isExpanded ? "Toca para ver menos" : "Toca para ver más"}
              </span>
              {alertInfo && (
                <span className={`justificacion-alert__badge ${alertInfo.className}`}>
                  {alertInfo.label}
                </span>
              )}
            </div>
          );
        },
      },
      { key: "responsable", header: "Responsable", width: 150, ellipsis: true },
      {
        key: "solicitud",
        header: "Solicitud",
        width: 220,
        mobileHidden: true,
        render: (r) => renderSolicitudSummary(r),
      },
      {
        key: "estado",
        header: "Estado",
        width: 120,
        render: (r) => {
          const isWithdrawn = Number(r.withdrawalStatus ?? 0) === 1;
          const current = r.estado || STATUS.PENDING;
          const isFinalLocked =
            current === STATUS.APPROVED || current === STATUS.DENIED;
          const canReopenApproved = isTicsAdmin(user, role) && current === STATUS.APPROVED;
          const stop = (ev) => ev.stopPropagation();

          const classFor = (label) =>
            "estado-pill " +
            (label === STATUS.PENDING
              ? "pending"
              : label === STATUS.APPROVED
              ? "ok"
              : "bad");

          const currentLabelEs = STATUS_LABELS_ES[current] || current;
          const CurrentIcon = STATUS_ICON[current] || FiClock;

          if (isWithdrawn) {
            return (
              <div className="estado-dropdown" onClick={stop}>
                <div className="estado-pill estado-pill--withdrawn">Retirado</div>
              </div>
            );
          }

          if (!canEditStatus || (isFinalLocked && !canReopenApproved)) {
            if (current === STATUS.APPROVED) {
              return (
                <span className="estado-final estado-final--approved">
                  <span className="estado-final__icon">
                    <CurrentIcon className="estado-icon" />
                  </span>
                  <span className="estado-final__label">{currentLabelEs}</span>
                </span>
              );
            }

            return (
              <span className={classFor(current)}>
                <span className="estado-with-icon">
                  <CurrentIcon className="estado-icon" />
                  {currentLabelEs}
                </span>
              </span>
            );
          }

          return (
            <div className="estado-dropdown" onClick={stop}>
              <Select
                value={current}
                onChange={(value) => changeEstado(r, value)}
                options={(canReopenApproved ? [STATUS.APPROVED, STATUS.PENDING] : STATUS_ORDER).map((value) => ({
                  value,
                  label: STATUS_LABELS_ES[value] || value,
                }))}
                className={`estado-select estado-select--${current}`}
                popupMatchSelectWidth={104}
                aria-label={`Estado de ${r.usuario || "la solicitud"}`}
              />
            </div>
          );
        },
      },
    ];

    if (canEditStatus) {
      const totalVisible = approvableVisibleRows.length;
      const selectedVisible = selectedApprovableVisibleRows.length;
      const allChecked = totalVisible > 0 && selectedVisible === totalVisible;
      const indeterminate = selectedVisible > 0 && selectedVisible < totalVisible;

      base.unshift({
        key: "bulkPick",
        header: (
          <Checkbox
            checked={allChecked}
            indeterminate={indeterminate}
            onChange={(e) => toggleAllVisible(e.target.checked)}
            disabled={totalVisible === 0 || bulkApproving}
          />
        ),
        width: "52px",
        align: "center",
        render: (r) => {
          const isWithdrawn = Number(r.withdrawalStatus ?? 0) === 1;
          const approvable = !isWithdrawn && (r.estado || STATUS.PENDING) === STATUS.PENDING;
          const checked = !excludedBulkKeys.includes(rowKeyOf(r));
          return (
            <Checkbox
              checked={approvable ? checked : false}
              disabled={!approvable || bulkApproving}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => toggleExcludeOne(r, e.target.checked)}
            />
          );
        },
      });
    }

    if (isManager) return base.filter((c) => c.key !== "responsable");
    return base;
  }, [
    isManager,
    role,
    user,
    canEditStatus,
    nowTs,
    fromDate,
    toDate,
    approvableVisibleRows,
    selectedApprovableVisibleRows,
    excludedBulkKeys,
    bulkApproving,
    expandedJustificationKeys,
  ]);

  const visibleColumns = useMemo(() => {
    return (columns || []).filter((c) => {
      if (c.key === "dni") return false;
      if (c.key === "phoneNumber") return false;
      if (c.key === "horasPicado") return false;
      return true;
    });
  }, [columns]);

  const desktopColumns = useMemo(() => {
    return visibleColumns.filter((c) => c.key !== "solicitud" && c.key !== "responsable");
  }, [visibleColumns]);

  const activeRangeLabel = useMemo(() => {
    const from = fmtFechaCorta(dateRange.from);
    const to = fmtFechaCorta(dateRange.to);
    if (!from || !to) return "";
    if (from === to) return `Mostrando: ${to}`;
    return `Mostrando: ${from} - ${to}`;
  }, [dateRange]);

  const loadWorkSummary = useCallback(async (row) => {
    const key = workSummaryKey(row);
    if (!key || workSummaryRequests.current.has(key)) return;
    if (parseHorasPreviewFromJustificacion(row?.justificacion, isOlvidePicarRow(row)).length) return;

    const date = row?.desde;
    if (!date) return;
    workSummaryRequests.current.add(key);
    setWorkSummaries((current) => ({ ...current, [key]: { loading: true } }));
    try {
      const workSummary = await fetchWorkSummaryPunches({
        dni: row.dni,
        date: date.toISOString().slice(0, 10),
      });
      setWorkSummaries((current) => ({ ...current, [key]: workSummary }));
    } catch (error) {
      console.error("No se pudo consultar horarios completos", error);
    }
  }, []);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (estadoFilter !== "all") chips.push({ key: "estado", label: `Estado: ${STATUS_LABELS_ES[estadoFilter] || estadoFilter}` });
    if (tipoFilter !== "all") chips.push({ key: "tipo", label: `Tipo: ${tipoFilter}` });
    if (responsableFilter !== "all") chips.push({ key: "responsable", label: `Responsable: ${responsableFilter}` });
    if (usuarioFilter !== "all") chips.push({ key: "usuario", label: `Usuario: ${usuarioFilter}` });
    if (globalQuery.trim()) chips.push({ key: "buscar", label: `Buscar: ${globalQuery.trim()}` });
    if (!isViewer && !showOlvidePicar) {
      chips.push({ key: "op", label: "No Pude Picar: oculto" });
    }
    return chips;
  }, [estadoFilter, tipoFilter, responsableFilter, usuarioFilter, showOlvidePicar, isViewer, globalQuery]);

  const clearAllFilters = () => {
    setEstadoFilter("all");
    setTipoFilter("all");
    setResponsableFilter("all");
    setUsuarioFilter("all");
    setGlobalQuery("");
    setShowOlvidePicar(true);
  };

  const toggleJustificationExpand = (row) => {
    const k = rowKeyOf(row);
    setExpandedJustificationKeys((prev) => {
      const set = new Set(prev);
      if (set.has(k)) set.delete(k);
      else set.add(k);
      return Array.from(set);
    });
  };

  const clearFilterChip = (key) => {
    if (key === "estado") setEstadoFilter("all");
    if (key === "tipo") setTipoFilter("all");
    if (key === "responsable") setResponsableFilter("all");
    if (key === "usuario") setUsuarioFilter("all");
    if (key === "buscar") setGlobalQuery("");
    if (key === "op") setShowOlvidePicar(true);
  };

  const ToolbarFilters = (
    <div className="period-tabs period-tabs--modern" onClick={(e) => e.stopPropagation()}>
      <div className="filters-modern__top">
        <div className="filters-modern__top-copy">
          <div className="filters-modern__range">{activeRangeLabel || "Rango activo"}</div>
        </div>

        <div className="filters-modern__top-right">
          <Input.Search
            value={globalQuery}
            onChange={(e) => setGlobalQuery(e.target.value)}
            allowClear
            placeholder="Buscar solicitudes"
            size="middle"
            className="filters-modern__top-search"
          />
          <Button icon={<FiFilter />} onClick={() => setFiltersOpen(true)}>
            Filtros{activeFilterChips.length ? ` (${activeFilterChips.length})` : ""}
          </Button>
          <div className="filters-modern__actions">
            {canEditStatus && (
              <button
                type="button"
                className="btn filters-modern__approve"
                onClick={handleBulkApproveVisible}
                disabled={bulkApproving}
              >
                {bulkApproving
                  ? "Aprobando..."
                  : `Aprobar seleccionados (${selectedApprovableVisibleRows.length})`}
              </button>
            )}

            <button
              type="button"
              className="btn primary filters-modern__report"
              onClick={() =>
                exportRangeToExcel(filteredRows, exportName, {
                  includeResponsable: !isManager,
                })
              }
            >
              <TbFileTypeXls className="btn-icon" />
              Generar reporte
            </button>
          </div>
        </div>
      </div>

        <Drawer
        title="Filtrar solicitudes"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        width={380}
        className="filters-drawer"
      >
                {activeFilterChips.length > 0 && (
                  <div className="filters-chips">
                    {activeFilterChips.map((chip) => (
                      <Tag
                        key={chip.key}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          clearFilterChip(chip.key);
                        }}
                        className="filter-tag"
                      >
                        {chip.label}
                      </Tag>
                    ))}
                    <Button type="text" className="filter-tag filter-tag--clear" onClick={clearAllFilters}>
                      Limpiar filtros
                    </Button>
                  </div>
                )}

                <div className="filters-modern__grid">
                  <div className="filter-inline filter-inline--date">
                    <span className="filter-inline__label">Desde</span>
                    <DatePicker
                      value={fromDate ? dayjs(fromDate) : null}
                      onChange={(d) => {
                        handleCalendarInteract();
                        if (!d) {
                          setFromDate(null);
                          setToDate(null);
                          return;
                        }
                        const nextFrom = d.toDate();
                        nextFrom.setHours(0, 0, 0, 0);
                        setFromDate(nextFrom);
                        setHighlightToDateStep(true);

                        if (!toDate || nextFrom.getTime() > toDate.getTime()) {
                          setToDate(nextFrom);
                        }
                      }}
                      onOpenChange={(open) => {
                        if (open) handleCalendarInteract();
                      }}
                      format="DD/MM/YYYY"
                      placeholder="Selecciona desde"
                      allowClear
                      className="campo-ant-picker"
                    />
                  </div>

                  <div
                    className={
                      "filter-inline filter-inline--date" +
                      (highlightToDateStep && fromDate ? " date-step-active" : "")
                    }
                  >
                    <span className="filter-inline__label">Hasta</span>
                    <DatePicker
                      value={toDate ? dayjs(toDate) : null}
                      onChange={(d) => {
                        handleCalendarInteract();
                        if (!d) {
                          setToDate(fromDate || null);
                          return;
                        }
                        const nextTo = d.toDate();
                        nextTo.setHours(0, 0, 0, 0);
                        setToDate(nextTo);
                      }}
                      onOpenChange={(open) => {
                        if (open) handleCalendarInteract();
                      }}
                      format="DD/MM/YYYY"
                      placeholder={fromDate ? "Selecciona hasta" : "Primero selecciona desde"}
                      allowClear={!!fromDate}
                      disabled={!fromDate}
                      minDate={fromDate ? dayjs(fromDate) : undefined}
                      className={
                        "campo-ant-picker" + (highlightToDateStep && fromDate ? " campo-ant-picker--active" : "")
                      }
                    />
                  </div>

                  <div className="filter-inline select--narrow">
                    <Select
                      value={estadoFilter === "all" ? undefined : estadoFilter}
                      onChange={(value) => setEstadoFilter(value || "all")}
                      options={[
                        {
                          value: STATUS.PENDING,
                          label: (
                            <span className="estado-with-icon">
                              <FiClock className="estado-icon" />
                              Pendiente
                            </span>
                          ),
                        },
                        {
                          value: STATUS.APPROVED,
                          label: (
                            <span className="estado-with-icon">
                              <FiCheckCircle className="estado-icon" />
                              Aprobado
                            </span>
                          ),
                        },
                        {
                          value: STATUS.DENIED,
                          label: (
                            <span className="estado-with-icon">
                              <FiXCircle className="estado-icon" />
                              Rechazado
                            </span>
                          ),
                        },
                      ]}
                      placeholder="Estado"
                      allowClear
                      className="campo-ant-select"
                    />
                  </div>

                  <div className="filter-inline select--mid">
                    <Select
                      value={tipoFilter === "all" ? undefined : tipoFilter}
                      onChange={(value) => setTipoFilter(value || "all")}
                      options={tipoOptions
                        .filter((x) => x !== "all")
                        .map((t) => ({ value: t, label: t }))}
                      placeholder="Tipo de permiso"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      className="campo-ant-select"
                    />
                  </div>

                  <div className="filter-inline select--responsable">
                    <Select
                      value={responsableFilter === "all" ? undefined : responsableFilter}
                      onChange={(value) => setResponsableFilter(value || "all")}
                      options={responsableOptions
                        .filter((x) => x !== "all")
                        .map((name) => ({ value: name, label: name }))}
                      placeholder="Responsable"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      className="campo-ant-select"
                    />
                  </div>

                  <div className="filter-inline select--usuario">
                    <Select
                      value={usuarioFilter === "all" ? undefined : usuarioFilter}
                      onChange={(value) => setUsuarioFilter(value || "all")}
                      options={usuarioOptions}
                      placeholder="Usuario"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      className="campo-ant-select"
                    />
                  </div>

                  {!isViewer && (
                    <div className="filter-inline filter-inline--toggle">
                      <Checkbox
                        checked={showOlvidePicar}
                        onChange={(e) => setShowOlvidePicar(e.target.checked)}
                      >
                        Mostrar No Pude Picar
                      </Checkbox>
                    </div>
                  )}
                </div>
      </Drawer>

      <Modal
        open={Boolean(bulkProgress)}
        closable={Boolean(bulkProgress?.done)}
        maskClosable={false}
        keyboard={Boolean(bulkProgress?.done)}
        footer={null}
        centered
        onCancel={() => bulkProgress?.done && setBulkProgress(null)}
      >
        {bulkProgress?.done ? (
          <Result
            status={bulkProgress.failed || bulkProgress.travelFailed ? "warning" : "success"}
            title="Aprobación finalizada"
            subTitle={`${bulkProgress.ok} aprobada(s) · ${bulkProgress.failed} con error${
              bulkProgress.travelOk || bulkProgress.travelFailed
                ? ` · Viajes: ${bulkProgress.travelOk} con asistencia actualizada${
                    bulkProgress.travelFailed ? `, ${bulkProgress.travelFailed} sin actualizar` : ""
                  }`
                : ""
            }`}
            extra={<Button type="primary" onClick={() => setBulkProgress(null)}>Cerrar</Button>}
          />
        ) : (
          <div className="bulk-progress">
            <div className="bulk-progress__title">Aprobando solicitudes</div>
            <div className="bulk-progress__user">{bulkProgress?.currentUser || "Preparando proceso..."}</div>
            <Progress
              percent={Math.round(((bulkProgress?.processed || 0) / (bulkProgress?.total || 1)) * 100)}
              status="active"
              strokeColor="#7a0a0a"
            />
            <div className="bulk-progress__count">
              {Math.floor(bulkProgress?.processed || 0)} de {bulkProgress?.total || 0}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
  const exportName = useMemo(() => {
    const dd = (d) => String(d.getDate()).padStart(2, "0");
    const mm = (d) => String(d.getMonth() + 1).padStart(2, "0");
    const yy = (d) => d.getFullYear();
    const a = dateRange.from
      ? `${dd(dateRange.from)}-${mm(dateRange.from)}-${yy(dateRange.from)}`
      : "inicio";
    const b = dateRange.to ? `${dd(dateRange.to)}-${mm(dateRange.to)}-${yy(dateRange.to)}` : "fin";
    return `asistencia_campo_${a}_a_${b}.xlsx`;
  }, [dateRange]);

  return (
    <ConfigProvider locale={esES}>
      <div className="page asistencia-campo-page">
        <div className="campo-hero">
          <div className="campo-hero__copy">
            <h1 className="page-title page-title--left">
              Control de asistencia a campo
            </h1>
          </div>
        </div>

        {error && <div className="campo-error">{error}</div>}

        <div className="campo-table-shell">
          {loading ? (
            <AsistenciaCampoSkeleton />
          ) : (
            <AttendanceTable
            columns={visibleColumns}
            desktopColumns={desktopColumns}
            rows={filteredRows}
            loading={loading}
            emptyText="Sin registros"
            toolbarLeft={ToolbarFilters}
            getRowKey={(r, i) => r.id ?? `${r.usuario}-${i}`}
            dense={false}
            mobileExpandableAfterKey="justificacion"
            expandable={{
              rowExpandable: () => true,
              expandedRowRender: (row) => renderRowDetails(row, workSummaries[workSummaryKey(row)]),
              onExpand: (expanded, row) => expanded && loadWorkSummary(row),
              expandRowByClick: true,
            }}
            pageSize={tablePageSize}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            onPageSizeChange={setTablePageSize}
            onVisibleRowsChange={setTableVisibleRows}
            />
          )}
        </div>

      </div>
    </ConfigProvider>
  );
}
